import React, { useRef } from 'react';
import { Download, Upload } from 'lucide-react';
import { CalendarEvent } from '../../hooks/useSupabaseEvents';

interface ImportExportProps {
  events: CalendarEvent[];
  onImport: (events: Partial<CalendarEvent>[]) => void;
}

export default function ImportExport({ events, onImport }: ImportExportProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const exportToICS = () => {
    const icsEvents = events.map(event => {
      const startDate = new Date(event.dates[0]);
      const endDate = event.dates.length > 1 
        ? new Date(event.dates[event.dates.length - 1])
        : startDate;
      
      // Add one day to end date for all-day events
      endDate.setDate(endDate.getDate() + 1);

      const formatDate = (date: Date) => {
        return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
      };

      return `BEGIN:VEVENT
UID:${event.id}@calendar.app
DTSTART;VALUE=DATE:${formatDate(startDate).substring(0, 8)}
DTEND;VALUE=DATE:${formatDate(endDate).substring(0, 8)}
SUMMARY:${event.title}
DESCRIPTION:${event.notes || ''}
END:VEVENT`;
    }).join('\n');

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Calendar App//EN
CALSCALE:GREGORIAN
${icsEvents}
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `calendar-${new Date().toISOString().split('T')[0]}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToJSON = () => {
    const json = JSON.stringify(events, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `calendar-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      
      if (file.name.endsWith('.json')) {
        try {
          const importedEvents = JSON.parse(content);
          onImport(importedEvents);
        } catch (error) {
          console.error('Error parsing JSON:', error);
          alert('Invalid JSON file');
        }
      } else if (file.name.endsWith('.ics')) {
        // Basic ICS parsing
        const events: Partial<CalendarEvent>[] = [];
        const eventMatches = content.matchAll(/BEGIN:VEVENT[\s\S]*?END:VEVENT/g);
        
        for (const match of eventMatches) {
          const eventText = match[0];
          const summaryMatch = eventText.match(/SUMMARY:(.+)/);
          const descriptionMatch = eventText.match(/DESCRIPTION:(.+)/);
          const startMatch = eventText.match(/DTSTART[^:]*:(\d{8})/);

          if (summaryMatch && startMatch) {
            const startDate = `${startMatch[1].substring(0, 4)}-${startMatch[1].substring(4, 6)}-${startMatch[1].substring(6, 8)}`;
            
            events.push({
              title: summaryMatch[1].trim(),
              notes: descriptionMatch?.[1].trim() || '',
              dates: [startDate], // Simplified - would need full date range logic
              color: { bg: 'bg-blue-500', name: 'Blue' }
            });
          }
        }
        
        if (events.length > 0) {
          onImport(events);
        } else {
          alert('No events found in ICS file');
        }
      }
    };
    
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  return (
    <div className="space-y-3">
      <div className="text-sm font-medium text-[var(--content-primary)] mb-2">Import/Export</div>

      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={exportToICS}
          className="px-3 py-2 bg-[var(--surface-secondary)] text-[var(--content-primary)] rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors text-sm flex items-center justify-center gap-2"
        >
          <Download className="w-4 h-4" />
          Export ICS
        </button>

        <button
          onClick={exportToJSON}
          className="px-3 py-2 bg-[var(--surface-secondary)] text-[var(--content-primary)] rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors text-sm flex items-center justify-center gap-2"
        >
          <Download className="w-4 h-4" />
          Export JSON
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".ics,.json"
        onChange={handleImport}
        className="hidden"
      />

      <button
        onClick={() => fileInputRef.current?.click()}
        className="w-full px-3 py-2 border border-[var(--border-primary)] text-[var(--content-primary)] rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors text-sm flex items-center justify-center gap-2"
      >
        <Upload className="w-4 h-4" />
        Import Calendar
      </button>
    </div>
  );
}